"use client";

import type { ActionButtonProps } from "@/components/ActionButton";
import { ActionGroup } from "@/components/ActionGroup";
import { Container } from "@/components/Container";
import {
	AdaptiveText,
	type AdaptiveTextProps,
	Title,
} from "@/components/Content";
import { Sizes } from "@/components/Content/styles";
import { Gradient } from "@/components/Gradient";
import { TypewriterWords } from "@/components/TypewriterWords";
import type { ImageDetails } from "@/types";
import { motion } from "framer-motion";
import Image from "next/image";

type Props = {
	id?: string;
	title?: string;
	highlightText?: string;
	titleSuffix?: string;
	flipWords?: string[];
	text?: AdaptiveTextProps["portableText"];
	actions?: ActionButtonProps[];
	image: ImageDetails;
};

const WithBottomImage = ({
	id,
	title,
	highlightText,
	titleSuffix,
	flipWords,
	text,
	actions,
	image,
}: Props) => {
	return (
		<section
			id={id}
			className="relative overflow-hidden pt-24 sm:pt-32 lg:pt-36"
		>
			<Gradient className="absolute inset-2 bottom-0 rounded-4xl opacity-30 ring-1 ring-black/20 ring-inset" />
			<Container className="relative">
				<motion.div
					className="pt-16 pb-24 sm:pt-24 sm:pb-32 md:pt-32 md:pb-0"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
				>
					<Title size={Sizes.Hero}>
						{title && (
							<>
								{title}
								{highlightText && (
									<span className="text-primary">{highlightText}</span>
								)}
								<br />
								{titleSuffix}
								{flipWords && (
									<TypewriterWords
										words={flipWords}
										typingSpeed={100}
										pauseDuration={300}
									/>
								)}
							</>
						)}
					</Title>
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
					>
						<AdaptiveText
							portableText={text}
							size={Sizes.Hero}
							className="max-w-lg"
						/>
					</motion.div>
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.4 }}
					>
						<ActionGroup className="mt-8 sm:mt-12" actions={actions} />
					</motion.div>
				</motion.div>
			</Container>

			<Container
				className="perspective-distant"
				containerClassName="hidden sm:block h-[60vh]"
			>
				<motion.div
					className="absolute isolate rounded-xl shadow-xl"
					style={{
						transformOrigin: "center",
						translateX: "30%",
					}}
					initial={{
						opacity: 0,
						y: 100,
						scale: 1.3,
						rotateX: 40,
						rotateY: 20,
						rotateZ: -30,
					}}
					animate={{
						opacity: 1,
						y: 0,
						scale: 1.3,
						rotateX: 40,
						rotateY: 20,
						rotateZ: -30,
					}}
					transition={{
						duration: 0.8,
						delay: 0.6,
						type: "spring",
						stiffness: 50,
					}}
				>
					<div className="-left-4 -z-10 absolute top-8 aspect-video h-full w-full rounded-xl bg-gray-50 shadow-xl" />
					<motion.div
						className="relative overflow-hidden rounded-xl"
						whileHover={{ scale: 1.02 }}
						transition={{ duration: 0.3 }}
					>
						<Image
							src={image.src}
							alt={image.alt || "Product interface"}
							width={3840}
							height={2160}
							className="size-full rounded-xl border border-black-100 object-cover shadow-xl"
							quality={100}
						/>
					</motion.div>
				</motion.div>
			</Container>
		</section>
	);
};

export default WithBottomImage;
